import { Alert, Button, Form, Select, Space } from "antd";
import type { Device } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { BaseItem as ZhBaseItem, Dialog as ZhDialog } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/stores/react/authStore";

export type ShipDeviceOption = Partial<Device> & {
    id: number | string;
    name?: string;
    serialNo?: string;
    position?: string;
};

/** 船舶详情页沿用的设备类型别名，便于旧设备查询契约平滑接入。 */
export type ShipDevice = ShipDeviceOption;

export interface ShipDeviceBindingPayload {
    id: number | string;
    type: number;
    masterId: number | string;
    position: string;
    transportType: number;
    flag: 0 | 1;
}

export interface ShipDeviceBindingModalProps {
    open: boolean;
    shipId: string;
    devices?: ShipDeviceOption[];
    loadDevices?: (shipId: string) => Promise<ShipDeviceOption[]>;
    loadDeviceOptions?: (keyword: string) => Promise<ShipDeviceOption[]>;
    bindDevice?: (payload: ShipDeviceBindingPayload) => Promise<unknown>;
    onSuccess?: (payload: ShipDeviceBindingPayload) => Promise<void> | void;
    onCancel: () => void;
    permissions?: string[];
}

const EMPTY_DEVICES: ShipDeviceOption[] = [];

/** 复用 Vue 设备选择器接口，按设备名称或序列号远程查询可绑定摄像头。 */
export async function loadShipDeviceOptions(keyword: string): Promise<ShipDeviceOption[]> {
    const response = await shippingRequestHttp.service.get<ShipDeviceOption[]>("/device/selector", {
        params: { keyword }
    });
    return response.data;
}

/** 复用 Vue 设备绑定/解绑接口，flag=1 绑定，flag=0 解绑。 */
export async function bindShipDevice(payload: ShipDeviceBindingPayload) {
    const response = await shippingRequestHttp.service.put("/device/binding-device", payload);
    return response.data;
}

function deviceLabel(device: ShipDeviceOption): string {
    return `${device.name || DEFAULT_VALUE} | ${device.serialNo || DEFAULT_VALUE}`;
}

/** 船舶设备管理弹窗，区分设备绑定与船舶认证绑定，避免复用错误的业务接口。 */
export function ShipDeviceBindingModal({
    open,
    shipId,
    devices = EMPTY_DEVICES,
    loadDevices,
    loadDeviceOptions = loadShipDeviceOptions,
    bindDevice = bindShipDevice,
    onSuccess,
    onCancel,
    permissions
}: ShipDeviceBindingModalProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canWrite =
        permissions === undefined || permissions.includes("ROLE_SHIP_W") || storePermissions.includes("ROLE_SHIP_W");
    const [currentDevices, setCurrentDevices] = useState<ShipDeviceOption[]>(devices);
    const [options, setOptions] = useState<ShipDeviceOption[]>([]);
    const [selectedId, setSelectedId] = useState<number | string>();
    const [selectedDevice, setSelectedDevice] = useState<ShipDeviceOption>();
    const [searching, setSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const effectiveDevices = devices.length ? devices : currentDevices;
    const boundDevice = effectiveDevices[0];
    const isBound = Boolean(boundDevice?.id);

    useEffect(() => {
        if (!open) return;
        let disposed = false;
        setOptions([]);
        setSelectedId(undefined);
        setSelectedDevice(undefined);
        setErrorMessage("");
        setCurrentDevices(devices);
        if (loadDevices) {
            void loadDevices(shipId)
                .then(result => {
                    if (!disposed) setCurrentDevices(result || []);
                })
                .catch(() => {
                    if (!disposed) setErrorMessage("设备查询失败");
                });
        }
        return () => {
            disposed = true;
        };
    }, [open, shipId, devices, loadDevices]);

    /** 仅在输入关键字后访问远程选择器，保持 Vue remote-select 行为。 */
    const handleSearch = async (keyword: string) => {
        if (!keyword.trim()) {
            setOptions([]);
            return;
        }
        setSearching(true);
        setErrorMessage("");
        try {
            setOptions((await loadDeviceOptions(keyword.trim())) || []);
        } catch {
            setErrorMessage("设备候选加载失败");
        } finally {
            setSearching(false);
        }
    };

    const handleSelect = (value: number | string) => {
        setSelectedId(value);
        setSelectedDevice(options.find(option => String(option.id) === String(value)));
        setErrorMessage("");
    };

    /** 生成 Vue ShipBindDevice 同构 payload，并在接口失败时保持弹窗打开。 */
    const handleSubmit = async () => {
        const target = isBound ? boundDevice : selectedDevice;
        if (!target?.id) {
            setErrorMessage("请选择设备");
            return;
        }
        const payload: ShipDeviceBindingPayload = {
            id: target.id,
            type: 1,
            masterId: shipId,
            position: target.position || "",
            transportType: 1,
            flag: isBound ? 0 : 1
        };
        setSubmitting(true);
        setErrorMessage("");
        try {
            await bindDevice(payload);
            onCancel();
            // 绑定接口已经成功，刷新详情失败不能再反向报告为绑定失败。
            try {
                await onSuccess?.(payload);
            } catch {
                // 详情刷新由父页面负责反馈，弹窗保持已关闭状态。
            }
        } catch {
            setErrorMessage(isBound ? "设备解绑失败" : "设备绑定失败");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ZhDialog
            open={open}
            title="船舶设备管理"
            modalRender={node => <div role="dialog">{node}</div>}
            onCancel={onCancel}
            closeOnClickModal={false}
            destroyOnHidden
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button
                        disabled={!canWrite}
                        type="primary"
                        loading={submitting}
                        onClick={() => void handleSubmit()}
                    >
                        {isBound ? "解绑" : "提交"}
                    </Button>
                </Space>
            }
        >
            {errorMessage ? <Alert title={errorMessage} role="alert" type="error" showIcon /> : null}
            {isBound ? (
                <ZhBaseItem label="关联设备" value={<span>[志豪链云] {boundDevice?.name || DEFAULT_VALUE}</span>} />
            ) : (
                <Form layout="vertical" className="mt-4">
                    <Form.Item label="关联设备" required>
                        <Select<number | string>
                            aria-label="关联设备"
                            showSearch
                            allowClear
                            filterOption={false}
                            value={selectedId}
                            placeholder="请输入设备名称或序列号"
                            loading={searching}
                            options={options.map(option => ({ label: deviceLabel(option), value: option.id }))}
                            onSearch={value => void handleSearch(value)}
                            onChange={handleSelect}
                            notFoundContent={searching ? "加载中" : "暂无设备"}
                        />
                    </Form.Item>
                </Form>
            )}
        </ZhDialog>
    );
}

export default ShipDeviceBindingModal;
